import { useMemo } from 'react';
import { Spin } from 'antd';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { ArrowFilled } from '@assets/icons/ArrowFilled';
import { UKRoundFlag } from '@assets/icons/UKRoundFlag';
import { USRoundFlag } from '@assets/icons/USRoundFlag';
import { Select } from '@components/Select';
import { useGetDefaultJobsBoardFilters } from '@hooks/jobsBoard/useGetDefaultJobsBoardFilters';
import { useGetJobsBoardLocations } from '@hooks/jobsBoard/useGetJobsBoardLocations';
import { useGetJobsBoardPosts } from '@hooks/jobsBoard/useGetJobsBoardPosts';
import { useGetJobsBoardPostsPublic } from '@hooks/jobsBoard/useGetJobsBoardPostsPublic';
import { JobsBoardPostsPublicResult } from '@hooks/jobsBoard/useGetJobsBoardPostsPublic/useGetJobsBoardPostsPublic';
import { buildModalSearchFiltersFromBackendPayload } from '@hooks/jobsBoard/utils';
import { useModalVisibility } from '@hooks/shared/useModalVisibility';
import { useUserCountry } from '@hooks/shared/useUserCountry';
import { RootState } from '@lib/redux.lib/store';

import {
  JobsBoardFiltersViewContextProvider,
  JobsBoardModalFilters,
  useJobsBoardFiltersViewContext,
} from '../context';

import { JobsBoardFilters } from './JobsBoardFilters';
import { JobsBoardPostsList } from './JobsBoardPosts';

export const JobsBoardSection = () => {
  const loggedInUserId = useSelector(
    (state: RootState) => state.app?.user?.profile?.id
  );
  const { userCountry } = useUserCountry();
  const {
    data: jobsBoardLocations,
    isLoading: isProjectLocationsLoading,
    isError: isProjectLocationsError,
  } = useGetJobsBoardLocations(null);

  const {
    data: defaultJobsBoardFilters,
    isLoading: isDefaultJobsBoardFiltersLoading,
    isError: isDefaultJobsBoardFiltersError,
  } = useGetDefaultJobsBoardFilters();

  const initialJobsBoardSearchFilters = useMemo(
    () =>
      loggedInUserId
        ? buildModalSearchFiltersFromBackendPayload(
            defaultJobsBoardFilters,
            userCountry,
            jobsBoardLocations
          )
        : {},
    [defaultJobsBoardFilters, loggedInUserId, userCountry, jobsBoardLocations]
  );

  if (
    loggedInUserId &&
    (isProjectLocationsLoading || isDefaultJobsBoardFiltersLoading)
  ) {
    return (
      <StyledContainer>
        <StyledTitleContainer>
          <h2>Job Board</h2>
        </StyledTitleContainer>
        <Spin />
      </StyledContainer>
    );
  }

  if (
    loggedInUserId &&
    (isProjectLocationsError ||
      isDefaultJobsBoardFiltersError ||
      !defaultJobsBoardFilters ||
      !jobsBoardLocations)
  ) {
    return (
      <StyledContainer>
        <StyledTitleContainer>
          <h2>Job Board</h2>
        </StyledTitleContainer>
        <div> Something went wrong</div>
      </StyledContainer>
    );
  }

  return (
    <JobsBoardFiltersViewContextProvider
      initialJobsBoardSearchFilters={initialJobsBoardSearchFilters}
    >
      <JobsBoardSectionDataLoader />
    </JobsBoardFiltersViewContextProvider>
  );
};

const JobsBoardSectionDataLoader = () => {
  const loggedInUserId = useSelector(
    (state: RootState) => state.app?.user?.profile?.id
  );

  const { publicViewCountry, jobsBoardSearchFilters } =
    useJobsBoardFiltersViewContext();

  if (loggedInUserId) {
    return (
      <JobsBoardSectionDataLoaderPrivate
        jobsBoardSearchFilters={jobsBoardSearchFilters}
        loggedInUserId={loggedInUserId}
        publicViewCountry={publicViewCountry}
      />
    );
  }

  return (
    <JobsBoardSectionDataLoaderPublic
      jobsBoardSearchFilters={jobsBoardSearchFilters}
      publicViewCountry={publicViewCountry}
      loggedInUserId={loggedInUserId}
    />
  );
};

type JobsBoardSectionDataLoaderProps = {
  jobsBoardSearchFilters: JobsBoardModalFilters;
  publicViewCountry: ProductionCountry;
  loggedInUserId: number | undefined;
};
const JobsBoardSectionDataLoaderPublic = ({
  jobsBoardSearchFilters,
  publicViewCountry,
}: JobsBoardSectionDataLoaderProps) => {
  const publicJobsBoardPostsInfo = useGetJobsBoardPostsPublic(
    jobsBoardSearchFilters,
    publicViewCountry
  );

  return (
    <JobsBoardSectionContent jobsBoardPostsInfo={publicJobsBoardPostsInfo} />
  );
};

const JobsBoardSectionDataLoaderPrivate = ({
  jobsBoardSearchFilters,
  loggedInUserId,
}: JobsBoardSectionDataLoaderProps) => {
  const privateJobsBoardPostsInfo = useGetJobsBoardPosts(
    jobsBoardSearchFilters
  );

  return (
    <JobsBoardSectionContent
      loggedInUserId={loggedInUserId}
      jobsBoardPostsInfo={privateJobsBoardPostsInfo}
    />
  );
};

type JobsBoardSectionContentProps = {
  jobsBoardPostsInfo: JobsBoardPostsPublicResult;
  loggedInUserId?: number;
};
const JobsBoardSectionContent = ({
  jobsBoardPostsInfo,
  loggedInUserId,
}: JobsBoardSectionContentProps) => {
  const { publicViewCountry, setPublicViewCountry, filtersCount } =
    useJobsBoardFiltersViewContext();
  const jobsFiltersModalVisibility = useModalVisibility();
  return (
    <StyledContainer>
      <StyledTitleContainer>
        <h2>Job Board</h2>
        {!loggedInUserId && (
          <StyledSelect
            suffixIcon={<ArrowFilled direction='down' />}
            options={selectOptions}
            value={{
              label: productionCountryToRoundedFlag[publicViewCountry],
              value: publicViewCountry,
            }}
            onChange={(value) => setPublicViewCountry(value)}
          />
        )}
      </StyledTitleContainer>
      <StyledFiltersWithPosts>
        <JobsBoardFilters
          totalJobsCount={jobsBoardPostsInfo?.data?.length}
          filtersCount={filtersCount}
          jobsFiltersModalVisibility={jobsFiltersModalVisibility}
        />
        <JobsBoardPostsList
          jobsFiltersModalVisibility={jobsFiltersModalVisibility}
          publicJobsBoardPostsInfo={jobsBoardPostsInfo}
        />
      </StyledFiltersWithPosts>
    </StyledContainer>
  );
};

const productionCountryToRoundedFlag: Record<ProductionCountry, JSX.Element> = {
  US: <USRoundFlag />,
  UK: <UKRoundFlag />,
};

const StyledOptionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const selectOptions: {
  label: JSX.Element;
  value: ProductionCountry;
}[] = [
  {
    label: (
      <StyledOptionItem>
        <USRoundFlag />
        <div>USA</div>
      </StyledOptionItem>
    ),
    value: 'US',
  },
  {
    label: (
      <StyledOptionItem>
        <UKRoundFlag />
        <div>UK</div>
      </StyledOptionItem>
    ),
    value: 'UK',
  },
];

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 32px 20px 32px;
  margin: 0 auto;
  max-width: 720px;

  @media (max-width: 768px) {
    gap: 20px;
    padding: 20px;
  }
`;

const StyledFiltersWithPosts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 768px) {
    gap: 16px;
  }
`;

const StyledTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  h2 {
    margin: 0;
    padding: 0;
    font-size: 22px;
    font-weight: 600;
    line-height: 27px;
  }
`;

const StyledSelect = styled(Select)`
  &.ant-select {
    .ant-select-selector {
      border: none;
      background-color: transparent;
      padding: 6px 8px;

      .ant-select-selection-item {
        padding-inline-end: 16px;
      }

      .ant-select-selection-search {
        display: none;
      }
    }

    .ant-select-arrow {
      width: 12px;
      inset-inline-end: 8px;
      height: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &.ant-select-focused {
      .ant-select-selector {
        border: none;
        box-shadow: none !important;
        background-color: transparent;
      }
    }
  }

  &.ant-select-dropdown {
    width: fit-content !important;
  }
`;
